<template>
  <div class="find-account-page">
    <LoadingOverlay :loading="submitting" message="กำลังค้นหาบัญชี..." />
    <div class="form-container">
      <v-card class="section-card" elevation="0">
        <h2 class="form-title mb-2">ค้นหาบัญชีของคุณ</h2>
        <p class="subtitle mb-6">โปรดป้อนอีเมลของคุณเพื่อค้นหาบัญชีของคุณ</p>

        <v-form ref="form" v-model="valid" @submit.prevent="onSearch">
          <v-row>
            <v-col cols="12">
              <span class="detail-text">อีเมล</span>
              <v-text-field
                v-model.trim="query"
                v-bind="tfProps"
                :rules="[rules.required, rules.email]"
                placeholder="ระบุอีเมล"
              />
            </v-col>

            <v-col cols="12" class="mt-2 d-flex justify-end btn-row">
              <v-btn class="btn-secondary" height="40" @click="onCancel">
                ยกเลิก
              </v-btn>
              <v-btn
                class="btn-primary"
                height="40"
                :disabled="!valid || submitting"
                type="submit"
                @click="onSearch"
              >
                ค้นหา
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </div>
  </div>
</template>

<script>
import { forgotPasswordRequestApi } from "@/services/api/auth";
import swalAlert from "@/services/alert/swalServices";
import LoadingOverlay from "@/components/LoadingOverlay.vue";

export default {
  name: "FindEmailPhone",
  components: {
    LoadingOverlay,
  },
  props: {
    accountType: { type: String, default: "user" },
  },

  data() {
    return {
      query: "",
      valid: false,
      submitting: false,
      tfProps: { outlined: true, hideDetails: "auto", dense: true },
      rules: {
        required: (v) => (!!v && String(v).trim() !== "") || "กรุณากรอกข้อมูล",
        email: (v) => {
          const val = String(v || "").trim();
          if (!val) return true;

          const emailValid =
            /^[A-Za-z0-9._-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(val);
          return emailValid || "อีเมลต้องอยู่ในรูปแบบ user@example.com";
        },
      },
    };
  },
  computed: {
    loginPath() {
      if (this.accountType === "seller") return "/information/login/seller";
      if (this.accountType === "rider") return "/information/login/rider";
      return "/information/login";
    },
  },
  methods: {
    onCancel() {
      if (this.$emit) this.$emit("cancel");
      if (this.$router) {
        this.$router.push(this.loginPath);
      } else {
        window.location.href = this.loginPath;
      }
    },
    async onSearch() {
      if (this.submitting) return;

      this.$refs.form.validate();
      if (!this.valid) return;

      this.submitting = true;
      try {
        const res = await forgotPasswordRequestApi(this.query, this.accountType);
        const message =
          res?.message || "ระบบได้ส่งลิงก์/รหัสสำหรับรีเซ็ตรหัสผ่านไปที่อีเมลแล้ว";

        await swalAlert.Success("ค้นหาบัญชีสำเร็จ", message);

        // แจ้ง parent component ให้ไป step ถัดไป พร้อม email ที่ค้นหา
        this.$emit("search", this.query);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "ไม่สามารถค้นหาบัญชีได้ กรุณาลองใหม่อีกครั้ง";
        await swalAlert.Error("ค้นหาบัญชีไม่สำเร็จ", msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.find-account-page {
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.form-container {
  width: 700px !important;
  max-width: 700px !important;
  margin: 0 auto;
}
.section-card {
  width: 100%;
  padding: 40px 30px;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(2, 6, 23, 0.06);
}
.form-title {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
  padding-top: 10px;
  padding-bottom: 5px;
}
.subtitle {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
}
.detail-text {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a39;
  line-height: 1.4;
  margin-bottom: 2px;
}
.req {
  color: #e11d48;
  margin-right: 4px;
}
:deep(.v-input--dense .v-input__slot) {
  min-height: 45px !important;
  height: 45px !important;
  padding: 0 10px !important;
}
:deep(.v-text-field.v-input--dense input) {
  height: 24px !important;
  line-height: 24px !important;
  padding: 0 !important;
  font-size: 14px !important;
}
:deep(.v-input) {
  margin-bottom: 8px !important;
}
:deep(.v-messages__message) {
  font-size: 12px !important;
  line-height: 1.4 !important;
}
.v-text-field--outlined >>> fieldset {
  border-color: #e5e7eb !important;
  border-radius: 8px !important;
}
.v-text-field--outlined.v-input.v-input--is-disabled >>> fieldset {
  border-color: #f3f5f7 !important;
}
.btn-row {
  gap: 12px;
  justify-content: flex-end;
}
.btn-primary {
  background: #F4811F !important;
  color: #fff !important;
  border-radius: 8px !important;
}
.btn-secondary {
  background: #f3f4f6 !important;
  color: #111827 !important;
  border-radius: 8px !important;
}
</style>
